/**
 * Deep clone helpers for AOM constraint trees (used by flattening / extraction).
 */

import * as openehr_am from "../openehr_am.ts";
import * as openehr_base from "../../base/openehr_base.ts";
import {
  COMPONENT_TERM_DEFINITIONS_KEY,
  TERM_ARCHETYPE_SCOPE_KEY,
  TERM_NAME_FALLBACK_NODE_ID_KEY,
  TERM_OCCURRENCE_SCOPE_KEY,
  type TermScopeMeta,
} from "../../generation/term_scope.ts";

function cloneMultiplicity(
  src: openehr_base.Multiplicity_interval | undefined,
): openehr_base.Multiplicity_interval | undefined {
  if (!src) return undefined;
  const m = new openehr_base.Multiplicity_interval();
  m.lower = src.lower;
  m.upper = src.upper;
  m.lower_unbounded = src.lower_unbounded;
  m.upper_unbounded = src.upper_unbounded;
  return m;
}

export function cloneCObject(obj: openehr_am.C_OBJECT): openehr_am.C_OBJECT {
  // C_ARCHETYPE_ROOT extends C_COMPLEX_OBJECT — cloneComplexObject handles both.
  if (obj instanceof openehr_am.C_COMPLEX_OBJECT) {
    return cloneComplexObject(obj);
  }
  if (obj instanceof openehr_am.ARCHETYPE_SLOT) {
    const slot = new openehr_am.ARCHETYPE_SLOT();
    copyObjectFields(obj, slot);
    const includes = (obj as { includes?: openehr_am.ARCHETYPE_ID_CONSTRAINT[] })
      .includes;
    if (includes) {
      (slot as { includes: openehr_am.ARCHETYPE_ID_CONSTRAINT[] }).includes =
        includes.map(cloneArchetypeIdConstraint);
    }
    const excludes = (obj as { excludes?: openehr_am.ARCHETYPE_ID_CONSTRAINT[] })
      .excludes;
    if (excludes) {
      (slot as { excludes: openehr_am.ARCHETYPE_ID_CONSTRAINT[] }).excludes =
        excludes.map(cloneArchetypeIdConstraint);
    }
    return slot;
  }
  if (obj instanceof openehr_am.C_PRIMITIVE_OBJECT) {
    // Keep the subclass (C_TERMINOLOGY_CODE, C_STRING, …) and its constraint
    // fields (`constraint`, `code_list`, `item`, `assumed_value`, …).
    return clonePrimitive(obj);
  }
  return obj;
}

/** Deep copy of a primitive constraint, preserving class instances. */
function clonePrimitive<T>(value: T): T {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(clonePrimitive) as T;
  if (value instanceof Date) return new Date(value.getTime()) as T;
  const out = Object.create(Object.getPrototypeOf(value));
  for (const [key, field] of Object.entries(value)) {
    out[key] = clonePrimitive(field);
  }
  return out;
}

function cloneArchetypeIdConstraint(
  c: openehr_am.ARCHETYPE_ID_CONSTRAINT,
): openehr_am.ARCHETYPE_ID_CONSTRAINT {
  const out = new openehr_am.ARCHETYPE_ID_CONSTRAINT();
  if (c.constraint) {
    const s = new openehr_am.C_STRING();
    s.pattern = c.constraint.pattern;
    out.constraint = s;
  }
  return out;
}

function copyObjectFields(
  src: openehr_am.C_OBJECT,
  dest: openehr_am.C_OBJECT,
): void {
  dest.rm_type_name = src.rm_type_name;
  dest.node_id = src.node_id;
  if (src.occurrences) {
    dest.occurrences = cloneMultiplicity(src.occurrences);
  }
  const srcMeta = src as TermScopeMeta;
  const destMeta = dest as TermScopeMeta;
  if (srcMeta[TERM_OCCURRENCE_SCOPE_KEY]) {
    destMeta[TERM_OCCURRENCE_SCOPE_KEY] = srcMeta[TERM_OCCURRENCE_SCOPE_KEY];
  }
  if (srcMeta[TERM_ARCHETYPE_SCOPE_KEY]) {
    destMeta[TERM_ARCHETYPE_SCOPE_KEY] = srcMeta[TERM_ARCHETYPE_SCOPE_KEY];
  }
  if (srcMeta[TERM_NAME_FALLBACK_NODE_ID_KEY]) {
    destMeta[TERM_NAME_FALLBACK_NODE_ID_KEY] =
      srcMeta[TERM_NAME_FALLBACK_NODE_ID_KEY];
  }
  if (srcMeta[COMPONENT_TERM_DEFINITIONS_KEY]) {
    destMeta[COMPONENT_TERM_DEFINITIONS_KEY] = {
      ...srcMeta[COMPONENT_TERM_DEFINITIONS_KEY],
    };
  }
}

function copyComplexFields(
  src: openehr_am.C_COMPLEX_OBJECT,
  dest: openehr_am.C_COMPLEX_OBJECT,
): void {
  copyObjectFields(src, dest);
  if (src.attributes) {
    dest.attributes = src.attributes.map(cloneAttribute);
  }
}

export function cloneComplexObject(
  obj: openehr_am.C_COMPLEX_OBJECT,
): openehr_am.C_COMPLEX_OBJECT {
  if (obj instanceof openehr_am.C_ARCHETYPE_ROOT) {
    const root = new openehr_am.C_ARCHETYPE_ROOT();
    copyComplexFields(obj, root);
    root.archetype_ref = obj.archetype_ref;
    return root;
  }
  const out = new openehr_am.C_COMPLEX_OBJECT();
  copyComplexFields(obj, out);
  return out;
}

export function cloneAttribute(
  attr: openehr_am.C_ATTRIBUTE,
): openehr_am.C_ATTRIBUTE {
  const out: openehr_am.C_ATTRIBUTE = attr instanceof
      openehr_am.C_MULTIPLE_ATTRIBUTE
    ? new openehr_am.C_MULTIPLE_ATTRIBUTE()
    : new openehr_am.C_SINGLE_ATTRIBUTE();

  out.rm_attribute_name = attr.rm_attribute_name;
  const existence = (attr as { existence?: openehr_base.Multiplicity_interval })
    .existence;
  if (existence) {
    (out as { existence: openehr_base.Multiplicity_interval }).existence =
      cloneMultiplicity(existence);
  }
  if (attr instanceof openehr_am.C_MULTIPLE_ATTRIBUTE && attr.cardinality) {
    const ma = out as openehr_am.C_MULTIPLE_ATTRIBUTE;
    const card = new openehr_am.CARDINALITY();
    const interval = (attr.cardinality as {
      interval?: openehr_base.Multiplicity_interval;
    }).interval ?? attr.cardinality.interval;
    if (interval) {
      (card as { interval: openehr_base.Multiplicity_interval }).interval =
        cloneMultiplicity(interval);
    }
    card.is_ordered = attr.cardinality.is_ordered;
    card.is_unique = (attr.cardinality as { is_unique?: boolean }).is_unique;
    ma.cardinality = card;
  }
  const children = (attr as { children?: openehr_am.C_OBJECT[] }).children;
  if (children) {
    (out as { children: openehr_am.C_OBJECT[] }).children = children.map(
      cloneCObject,
    );
  }
  return out;
}
